import { useState, useEffect } from "react";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchEmployeeName } from "@/services/employeeService";
import { updateEvaluationStep } from "@/services/evaluationService";
import { parseAssessmentState } from "@/utils/locationState";

interface StepNavigationProps {
  currentStep?: number;
  compact?: boolean;
  employeeId?: string | null;
}

export default function StepNavigation({ currentStep = 2, compact = false, employeeId }: StepNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [employeeFirstName, setEmployeeFirstName] = useState<string | null>(null);

  const currentState = parseAssessmentState(location.state);
  const navState = {
    employeeId: employeeId || currentState?.employeeId || null,
    competencyIds: currentState?.competencyIds || null,
    evaluatedCompetencyIds: currentState?.evaluatedCompetencyIds || null,
    evaluationId: currentState?.evaluationId || null,
  };

  const resolvedEmployeeId = navState.employeeId;

  useEffect(() => {
    if (!resolvedEmployeeId) return;
    fetchEmployeeName(resolvedEmployeeId).then((data) => {
      if (data) setEmployeeFirstName(data.first_name);
    });
  }, [resolvedEmployeeId]);

  const meetLabel = employeeFirstName
    ? `${t('steps.meet')} ${employeeFirstName}`
    : t('steps.meet');

  const steps = [
    { id: 1, label: t('steps.evaluation'), href: "/evaluation" },
    { id: 2, label: t('steps.results'), href: "/assessment-results" },
    { id: 3, label: t('steps.prepareMeeting'), href: "/prepare-meeting" },
    { id: 4, label: meetLabel, href: "/meet-employee" },
  ];

  const handleStepClick = async (step: typeof steps[0]) => {
    const evalId = navState.evaluationId;
    if (evalId) {
      await updateEvaluationStep(evalId, step.id);
    }
    navigate(step.href, { state: navState });
  };

  if (compact) {
    return (
      <div className="w-full flex flex-col gap-1 md:gap-2">
        {/* Steps Container - Compact */}
        <div className="flex items-center gap-0.5 md:gap-1">
          {steps.map((step, index) => {
            const isCompleted = step.id < currentStep;
            const isActive = step.id === currentStep;

            return (
              <div key={step.id} className="flex items-center gap-0.5 md:gap-1 flex-1 min-w-0">
                {/* Step Button - Compact */}
                <div className="flex flex-col items-center gap-0.5 md:gap-1 flex-1 min-w-0">
                  <button
                    onClick={() => handleStepClick(step)}
                    className={`relative w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer ${
                      isCompleted
                        ? "bg-gradient-to-br from-emerald-400 to-teal-500 border-emerald-500 shadow-md shadow-emerald-200"
                        : isActive
                          ? "bg-gradient-to-br from-turquoise to-blue-500 border-turquoise shadow-lg shadow-turquoise/30 ring-2 ring-turquoise/20"
                          : "bg-white border-gray-300 shadow-sm hover:shadow-md hover:border-gray-400"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-white" strokeWidth={2.5} />
                    ) : (
                      <span
                        className={`text-[9px] md:text-[10px] font-bold transition-colors ${
                          isActive ? "text-white" : "text-gray-400"
                        }`}
                      >
                        {step.id}
                      </span>
                    )}
                  </button>

                  {/* Label - Compact and inline */}
                  <span
                    className={`text-[9px] md:text-[11px] font-semibold text-center truncate max-w-full transition-colors leading-tight ${
                      isActive
                        ? "text-turquoise"
                        : isCompleted
                          ? "text-teal-700"
                          : "text-gray-600"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Chevron separator (not on last step) */}
                {index < steps.length - 1 && (
                  <ChevronRight
                    className={`w-2.5 h-2.5 md:w-3 md:h-3 transition-colors flex-shrink-0 -mx-0.5 ${
                      isCompleted ? "text-emerald-500" : "text-gray-300"
                    }`}
                    strokeWidth={2.5}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress Bar - Compact */}
        <div className="w-full bg-gray-200/60 rounded-full h-1 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-turquoise via-blue-500 to-purple-500 rounded-full shadow-lg shadow-turquoise/30"
            style={{
              width: `${((currentStep - 0.5) / steps.length) * 100}%`,
              transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              willChange: "width",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Container */}
      <div>
        {/* Steps Container */}
        <div className="flex items-center gap-1 md:gap-2 mb-3 md:mb-5">
          {steps.map((step, index) => {
            const isCompleted = step.id < currentStep;
            const isActive = step.id === currentStep;

            return (
              <div key={step.id} className="flex items-center gap-1 md:gap-2 flex-1 min-w-0">
                {/* Step Button */}
                <div className="relative flex flex-col items-center flex-1 min-w-0">
                  <button
                    onClick={() => handleStepClick(step)}
                    className={`relative w-8 h-8 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer ${
                      isCompleted
                        ? "bg-gradient-to-br from-emerald-400 to-teal-500 border-emerald-500 shadow-lg shadow-emerald-200"
                        : isActive
                          ? "bg-gradient-to-br from-turquoise to-blue-500 border-turquoise shadow-xl shadow-turquoise/30 ring-4 ring-turquoise/20"
                          : "bg-white border-gray-300 shadow-sm hover:shadow-md hover:border-gray-400"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4 md:w-7 md:h-7 text-white" strokeWidth={2.5} />
                    ) : (
                      <span
                        className={`text-sm md:text-lg font-bold transition-colors ${
                          isActive ? "text-white" : "text-gray-400"
                        }`}
                      >
                        {step.id}
                      </span>
                    )}
                  </button>

                  {/* Label below */}
                  <span
                    className={`text-[10px] md:text-xs font-semibold text-center truncate max-w-full mt-1 md:mt-2 transition-colors ${
                      isActive
                        ? "text-turquoise"
                        : isCompleted
                          ? "text-teal-700"
                          : "text-gray-600"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Chevron separator (not on last step) */}
                {index < steps.length - 1 && (
                  <ChevronRight
                    className={`w-3 h-3 md:w-5 md:h-5 transition-colors flex-shrink-0 -ml-0.5 md:-ml-1 ${
                      isCompleted ? "text-emerald-500" : "text-gray-300"
                    }`}
                    strokeWidth={2}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200/60 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-turquoise via-blue-500 to-purple-500 rounded-full shadow-lg shadow-turquoise/30"
            style={{
              width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
              transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              willChange: "width",
            }}
          />
        </div>

        {/* Progress text */}
        <div className="flex justify-between items-center mt-2 md:mt-3">
          <span className="text-[10px] md:text-xs text-gray-500 font-medium">
            {t('steps.progress', { current: currentStep, total: steps.length })}
          </span>
          <span className="text-xs font-bold text-turquoise">
            {t('steps.completed', { percent: Math.round(((currentStep - 1) / (steps.length - 1)) * 100) })}
          </span>
        </div>
      </div>
    </div>
  );
}
